// frontend/src/services/ambulanceService.js - UPDATED with maximumAge: 0
import apiService from './api';

/**
 * Ambulance Service - Handles all ambulance-related API calls
 */
export const ambulanceService = {
  /**
   * Get current user location - ALWAYS asks for fresh permission
   * @returns {Promise<{success: boolean, data?: object, message?: string}>}
   */
  getCurrentLocation: () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve({
          success: false,
          message: 'Geolocation is not supported by your browser',
          suggestions: ['Please enter your location manually', 'Update to a modern browser']
        });
        return;
      }

      // CRITICAL: maximumAge: 0 ensures fresh permission request
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            success: true,
            data: {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy
            }
          });
        },
        (error) => {
          let message = 'Unable to get your location';
          const suggestions = [];

          switch (error.code) {
            case error.PERMISSION_DENIED:
              message = 'Location access denied. Please enable location permissions.';
              suggestions.push('Click the location icon in your browser\'s address bar');
              suggestions.push('Select "Allow" for location permissions');
              suggestions.push('Enable location in browser settings');
              suggestions.push('Try entering your location manually');
              break;
            case error.POSITION_UNAVAILABLE:
              message = 'Location information unavailable';
              suggestions.push('Check your device GPS settings');
              suggestions.push('Ensure location services are enabled');
              suggestions.push('Try again in a moment');
              break;
            case error.TIMEOUT:
              message = 'Location request timed out';
              suggestions.push('Check your internet connection');
              suggestions.push('Try again');
              break;
            default:
              message = 'An unknown error occurred';
          }

          resolve({
            success: false,
            message,
            suggestions
          });
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0  // 🔥 CRITICAL: Always get fresh location, no cache
        }
      );
    });
  },

  /**
   * Watch user location in real-time
   * @param {Function} callback - Callback function called on location update
   * @returns {number} Watch ID for clearing
   */
  watchLocation: (callback) => {
    if (!navigator.geolocation) {
      callback({
        success: false,
        message: 'Geolocation is not supported'
      });
      return null;
    }

    return navigator.geolocation.watchPosition(
      (position) => {
        callback({
          success: true,
          data: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: position.timestamp
          }
        });
      },
      (error) => {
        callback({
          success: false,
          message: 'Failed to get location',
          error
        });
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0  // 🔥 Always get fresh location for watch too
      }
    );
  },

  /**
   * Stop watching location
   * @param {number} watchId - Watch ID from watchLocation
   */
  clearLocationWatch: (watchId) => {
    if (watchId && navigator.geolocation) {
      navigator.geolocation.clearWatch(watchId);
    }
  },

  /**
   * Get all ambulances with filters
   * @param {Object} params - Query parameters
   * @returns {Promise}
   */
  getAll: async (params = {}) => {
    try {
      return await apiService.ambulances.getAll(params);
    } catch (error) {
      console.error('Get ambulances error:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch ambulances'
      };
    }
  },

  /**
   * Get nearby ambulances
   * @param {Object} params - Location and filter parameters
   * @returns {Promise}
   */
  getNearby: async (params = {}) => {
    try {
      const { latitude, longitude, radius = 50, serviceType, limit = 20 } = params;

      if (!latitude || !longitude) {
        return {
          success: false,
          message: 'Location coordinates are required'
        };
      }

      const queryParams = {
        lat: latitude,
        lng: longitude,
        radius,
        limit
      };

      if (serviceType) {
        queryParams.serviceType = serviceType;
      }

      return await apiService.ambulances.getNearby(queryParams);
    } catch (error) {
      console.error('Get nearby ambulances error:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch nearby ambulances'
      };
    }
  },

  /**
   * Get ambulance by ID
   * @param {string} id - Ambulance ID
   * @returns {Promise}
   */
  getById: async (id) => {
    try {
      if (!id) {
        return {
          success: false,
          message: 'Ambulance ID is required'
        };
      }

      return await apiService.ambulances.getById(id);
    } catch (error) {
      console.error('Get ambulance by ID error:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch ambulance details'
      };
    }
  },

  /**
   * Request ambulance
   * @param {Object} requestData - Request details
   * @returns {Promise}
   */
  requestAmbulance: async (requestData) => {
    try {
      const {
        ambulanceId,
        pickupLocation,
        dropoffLocation,
        patientInfo,
        requestType = 'emergency',
        priority = 'high',
        specialRequirements
      } = requestData;

      // Validate required fields
      if (!pickupLocation || !pickupLocation.coordinates || !pickupLocation.address) {
        return {
          success: false,
          message: 'Pickup location is required'
        };
      }

      if (!dropoffLocation || !dropoffLocation.coordinates || !dropoffLocation.address) {
        return {
          success: false,
          message: 'Dropoff location is required'
        };
      }

      if (!patientInfo || !patientInfo.name || !patientInfo.condition) {
        return {
          success: false,
          message: 'Patient information is required'
        };
      }

      // Calculate estimated distance
      const distance = calculateDistance(
        pickupLocation.coordinates[1],
        pickupLocation.coordinates[0],
        dropoffLocation.coordinates[1],
        dropoffLocation.coordinates[0]
      );

      const payload = {
        ambulance: ambulanceId,
        requestType,
        priority,
        patientInfo: {
          name: patientInfo.name,
          age: patientInfo.age,
          gender: patientInfo.gender,
          phone: patientInfo.phone,
          condition: patientInfo.condition,
          symptoms: patientInfo.symptoms,
          isConscious: patientInfo.isConscious,
          canWalk: patientInfo.canWalk
        },
        pickupLocation: {
          type: 'Point',
          coordinates: pickupLocation.coordinates,
          address: pickupLocation.address,
          landmark: pickupLocation.landmark,
          instructions: pickupLocation.instructions
        },
        dropoffLocation: {
          type: 'Point',
          coordinates: dropoffLocation.coordinates,
          address: dropoffLocation.address,
          facilityName: dropoffLocation.facilityName,
          contactPerson: dropoffLocation.contactPerson,
          contactPhone: dropoffLocation.contactPhone
        },
        estimatedDistance: distance,
        specialRequirements
      };

      const response = await apiService.post('/ambulance-requests', payload);
      return response;
    } catch (error) {
      console.error('Request ambulance error:', error);
      return {
        success: false,
        message: error.message || 'Failed to request ambulance. Please try calling directly.'
      };
    }
  },

  /**
   * Get user's ambulance requests
   * @param {Object} params - Query parameters
   * @returns {Promise}
   */
  getMyRequests: async (params = {}) => {
    try {
      const queryParams = {
        page: params.page || 1,
        limit: params.limit || 20
      };

      if (params.status) {
        queryParams.status = params.status;
      }

      const response = await apiService.get('/ambulance-requests/my-requests', { params: queryParams });
      return response;
    } catch (error) {
      console.error('Get my requests error:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch your requests'
      };
    }
  },

  /**
   * Get request by ID
   * @param {string} requestId - Request ID
   * @returns {Promise}
   */
  getRequestById: async (requestId) => {
    try {
      if (!requestId) {
        return {
          success: false,
          message: 'Request ID is required'
        };
      }

      const response = await apiService.get(`/ambulance-requests/${requestId}`);
      return response;
    } catch (error) {
      console.error('Get request by ID error:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch request details'
      };
    }
  },

  /**
   * Get active requests with live tracking data
   * @param {Object} params - Query parameters
   * @returns {Promise}
   */
  getActiveRequests: async (params = {}) => {
    try {
      const activeStatuses = [
        'accepted',
        'en_route_pickup',
        'arrived_pickup',
        'patient_loaded',
        'en_route_dropoff',
        'arrived_dropoff'
      ];

      const queryParams = {
        status: params.status || activeStatuses.join(','),
        page: params.page || 1,
        limit: params.limit || 50
      };

      const response = await apiService.ambulances.getRequests(queryParams);
      return response;
    } catch (error) {
      console.error('Get active requests error:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch active requests'
      };
    }
  },

  /**
   * Update request status
   * @param {string} requestId - Request ID
   * @param {Object} statusData - Status update data
   * @returns {Promise}
   */
  updateRequestStatus: async (requestId, statusData) => {
    try {
      if (!requestId) {
        return {
          success: false,
          message: 'Request ID is required'
        };
      }

      const { status, notes, location } = statusData;

      if (!status) {
        return {
          success: false,
          message: 'Status is required'
        };
      }

      const response = await apiService.put(
        `/ambulances/me/requests/${requestId}/update-status`,
        { status, notes, location }
      );

      return response;
    } catch (error) {
      console.error('Update request status error:', error);
      return {
        success: false,
        message: error.message || 'Failed to update status'
      };
    }
  },

  /**
   * Update vehicle location
   * @param {string} vehicleId - Vehicle ID
   * @param {Object} locationData - Location data
   * @returns {Promise}
   */
  updateVehicleLocation: async (vehicleId, locationData) => {
    try {
      if (!vehicleId) {
        return {
          success: false,
          message: 'Vehicle ID is required'
        };
      }

      const { latitude, longitude, heading, speed } = locationData;

      if (!latitude || !longitude) {
        return {
          success: false,
          message: 'Location coordinates are required'
        };
      }

      const response = await apiService.patch(
        `/ambulances/me/vehicles/${vehicleId}/location`,
        {
          location: {
            type: 'Point',
            coordinates: [longitude, latitude]
          },
          heading,
          speed,
          timestamp: new Date().toISOString()
        }
      );

      return response;
    } catch (error) {
      console.error('Update vehicle location error:', error);
      return {
        success: false,
        message: error.message || 'Failed to update location'
      };
    }
  },

  /**
   * Get live location updates for a request
   * @param {string} requestId - Request ID
   * @returns {Promise}
   */
  getLiveLocation: async (requestId) => {
    try {
      if (!requestId) {
        return {
          success: false,
          message: 'Request ID is required'
        };
      }

      const response = await apiService.get(`/ambulance-requests/${requestId}/location`);
      return response;
    } catch (error) {
      console.error('Get live location error:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch location'
      };
    }
  },

  /**
   * Cancel ambulance request
   * @param {string} requestId - Request ID
   * @param {string} reason - Cancellation reason
   * @returns {Promise}
   */
  cancelRequest: async (requestId, reason) => {
    try {
      if (!requestId) {
        return {
          success: false,
          message: 'Request ID is required'
        };
      }

      const response = await apiService.put(`/ambulance-requests/${requestId}/cancel`, {
        reason: reason || 'Cancelled by user'
      });
      return response;
    } catch (error) {
      console.error('Cancel request error:', error);
      return {
        success: false,
        message: error.message || 'Failed to cancel request'
      };
    }
  },

  /**
   * Rate ambulance service
   * @param {string} requestId - Request ID
   * @param {Object} ratingData - Rating information
   * @returns {Promise}
   */
  rateService: async (requestId, ratingData) => {
    try {
      if (!requestId) {
        return {
          success: false,
          message: 'Request ID is required'
        };
      }

      if (!ratingData.score || ratingData.score < 1 || ratingData.score > 5) {
        return {
          success: false,
          message: 'Rating must be between 1 and 5'
        };
      }

      const response = await apiService.post(`/ambulance-requests/${requestId}/rate`, {
        score: ratingData.score,
        review: ratingData.review || ''
      });
      return response;
    } catch (error) {
      console.error('Rate service error:', error);
      return {
        success: false,
        message: error.message || 'Failed to submit rating'
      };
    }
  },

  /**
   * Get ETA (Estimated Time of Arrival)
   * @param {Object} params - ETA calculation parameters
   * @returns {Promise}
   */
  getETA: async (params) => {
    try {
      const { fromLat, fromLng, toLat, toLng, mode = 'driving' } = params;

      if (!fromLat || !fromLng || !toLat || !toLng) {
        return {
          success: false,
          message: 'Complete location coordinates are required'
        };
      }

      // Calculate straight-line distance
      const distance = calculateDistance(fromLat, fromLng, toLat, toLng);
      
      // Estimate time based on average speed (40 km/h in traffic)
      const averageSpeed = 40;
      const timeInHours = distance / averageSpeed;
      const timeInMinutes = Math.ceil(timeInHours * 60);

      return {
        success: true,
        data: {
          distance: distance,
          duration: timeInMinutes,
          estimatedArrival: new Date(Date.now() + timeInMinutes * 60000).toISOString()
        }
      };
    } catch (error) {
      console.error('Get ETA error:', error);
      return {
        success: false,
        message: error.message || 'Failed to calculate ETA'
      };
    }
  },

  /**
   * Send emergency alert
   * @param {Object} alertData - Emergency alert data
   * @returns {Promise}
   */
  sendEmergencyAlert: async (alertData) => {
    try {
      const { requestId, message, location } = alertData;

      if (!requestId) {
        return {
          success: false,
          message: 'Request ID is required'
        };
      }

      const response = await apiService.post('/ambulance-requests/emergency-alert', {
        requestId,
        message: message || 'Emergency alert',
        location,
        timestamp: new Date().toISOString()
      });

      return response;
    } catch (error) {
      console.error('Send emergency alert error:', error);
      return {
        success: false,
        message: error.message || 'Failed to send alert'
      };
    }
  },

  /**
   * Get request statistics
   * @param {Object} params - Filter parameters
   * @returns {Promise}
   */
  getRequestStats: async (params = {}) => {
    try {
      const response = await apiService.ambulances.getStats();
      return response;
    } catch (error) {
      console.error('Get request stats error:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch statistics'
      };
    }
  },

  /**
   * Get route between two points
   * @param {Object} params - Route parameters
   * @returns {Promise}
   */
  getRoute: async (params) => {
    try {
      const { fromLat, fromLng, toLat, toLng } = params;

      if (!fromLat || !fromLng || !toLat || !toLng) {
        return {
          success: false,
          message: 'Complete location coordinates are required'
        };
      }

      // This is a placeholder - integrate with actual routing service
      // (Google Maps Directions API, Mapbox Directions API, etc.)
      
      const distance = calculateDistance(fromLat, fromLng, toLat, toLng);
      
      return {
        success: true,
        data: {
          distance: distance,
          route: [
            [fromLng, fromLat],
            [toLng, toLat]
          ],
          steps: [
            {
              instruction: 'Head towards destination',
              distance: distance
            }
          ]
        }
      };
    } catch (error) {
      console.error('Get route error:', error);
      return {
        success: false,
        message: error.message || 'Failed to calculate route'
      };
    }
  },

  /**
   * Validate coordinates
   * @param {number} lat - Latitude
   * @param {number} lng - Longitude
   * @returns {boolean}
   */
  isValidCoordinates: (lat, lng) => {
    return (
      typeof lat === 'number' &&
      typeof lng === 'number' &&
      lat >= -90 &&
      lat <= 90 &&
      lng >= -180 &&
      lng <= 180
    );
  },

  /**
   * Format coordinates for display
   * @param {number} lat - Latitude
   * @param {number} lng - Longitude
   * @returns {string}
   */
  formatCoordinates: (lat, lng) => {
    if (!ambulanceService.isValidCoordinates(lat, lng)) {
      return 'Invalid coordinates';
    }

    const latDir = lat >= 0 ? 'N' : 'S';
    const lngDir = lng >= 0 ? 'E' : 'W';

    return `${Math.abs(lat).toFixed(6)}°${latDir}, ${Math.abs(lng).toFixed(6)}°${lngDir}`;
  },

  /**
   * Calculate bearing between two points
   * @param {number} lat1 - Start latitude
   * @param {number} lon1 - Start longitude
   * @param {number} lat2 - End latitude
   * @param {number} lon2 - End longitude
   * @returns {number} Bearing in degrees
   */
  calculateBearing: (lat1, lon1, lat2, lon2) => {
    const dLon = toRad(lon2 - lon1);
    const y = Math.sin(dLon) * Math.cos(toRad(lat2));
    const x = Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) -
              Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(dLon);
    const bearing = Math.atan2(y, x);
    return (toDeg(bearing) + 360) % 360;
  },

  /**
   * Get cardinal direction from bearing
   * @param {number} bearing - Bearing in degrees
   * @returns {string} Cardinal direction
   */
  getCardinalDirection: (bearing) => {
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    const index = Math.round(bearing / 45) % 8;
    return directions[index];
  }
};

/**
 * Calculate distance between two coordinates (Haversine formula)
 * @param {number} lat1 - Latitude 1
 * @param {number} lon1 - Longitude 1
 * @param {number} lat2 - Latitude 2
 * @param {number} lon2 - Longitude 2
 * @returns {number} Distance in kilometers
 */
function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371; // Earth's radius in km
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Convert degrees to radians
 * @param {number} degrees - Degrees
 * @returns {number} Radians
 */
function toRad(degrees) {
  return degrees * (Math.PI / 180);
}

/**
 * Convert radians to degrees
 * @param {number} radians - Radians
 * @returns {number} Degrees
 */
function toDeg(radians) {
  return radians * (180 / Math.PI);
}

export default ambulanceService;